import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { Notification, TickCircle, CloseCircle, Lock1 } from 'iconsax-react';
import { LockScreenHyperframe } from './LockScreenHyperframe';
import { ExternalBrowserHandoff, IosInstallGuide } from './IosInstallGuide';
import { ThreadsSafariGuide } from './ThreadsSafariGuide';
import {
    ensurePushSubscription,
    getPushSupport,
    isStandalone,
    logPushFunnelEvent,
    setPushModalVisible,
    showTestNotification,
    isThreadsInAppBrowser,
} from '../../lib/webPush';

/**
 * 웹 푸시 프리퍼미션(사전 허용 유도) 모달 — 실구현
 *
 * 브라우저의 진짜 권한 팝업은 사이트당 사실상 1회성 자원이다 — 사용자가 "차단"을
 * 누르면 코드로는 다시 물어볼 수 없다. 그래서 진짜 팝업을 띄우기 전에 우리 화면으로
 * 먼저 의사를 묻고, "받을게요"를 누른 사람에게만 브라우저 팝업을 노출한다.
 * "나중에"는 브라우저에 기록이 남지 않으므로 다음 방문에 다시 물어볼 수 있다.
 *
 * 플랫폼 분기:
 * - 안드로이드/데스크톱: 권한 팝업 → 허용 시 즉시 구독 생성 + 서버 저장
 * - iOS Safari(미설치): 홈 화면 설치 안내로 분기 — 설치 후 standalone 첫 실행에서
 *   PushPermissionGate가 이 모달을 다시 띄워 허용까지 완주시킨다
 *
 * 모든 단계는 push_funnel_events에 기록된다 (관리자 추적: /admin/web-push).
 */

type Step = 'pitch' | 'finish' | 'ios-install' | 'external' | 'granted' | 'denied' | 'unsupported' | 'error';

interface PushPrePermissionModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** 퍼널 이벤트에 기록될 유입 경로: organic | alimtalk | standalone | demo 등 */
    source?: string;
}

const HEADER_COPY: Record<Step, { title: string; subtitle: string }> = {
    pitch: {
        title: '이런 알림이 실시간으로 도착해요',
        subtitle: '누군가 프로필을 다녀가는 순간, 바로 알 수 있어요',
    },
    finish: {
        title: '설치 완료! 이제 마지막 한 번이에요',
        subtitle: '이미 동의하셨으니, 확인 한 번만 남았어요',
    },
    'ios-install': {
        title: '홈 화면에 추가하면 알림을 받을 수 있어요',
        subtitle: 'iPhone은 딱 한 번, 10초면 끝나요',
    },
    external: {
        title: '기본 브라우저에서 계속해주세요',
        subtitle: '인앱 브라우저는 알림을 지원하지 않아요',
    },
    granted: {
        title: '알림이 켜졌어요',
        subtitle: '이제 변화가 생기면 실시간으로 알려드려요',
    },
    denied: {
        title: '알림이 차단되어 있어요',
        subtitle: '아래 방법으로 브라우저에서 직접 켤 수 있어요',
    },
    unsupported: {
        title: '이 브라우저는 알림을 지원하지 않아요',
        subtitle: 'Chrome, Safari 최신 버전에서 이용할 수 있어요',
    },
    error: {
        title: '설정을 마치지 못했어요',
        subtitle: '일시적인 문제일 수 있어요 — 다시 시도해주세요',
    },
};

export const PushPrePermissionModal: React.FC<PushPrePermissionModalProps> = ({
    isOpen,
    onClose,
    source = 'organic',
}) => {
    const [step, setStep] = useState<Step>('pitch');
    const [busy, setBusy] = useState(false);
    const [needsSafari, setNeedsSafari] = useState(false);
    const shownLoggedRef = useRef(false);

    useEffect(() => {
        if (isOpen) {
            // 설치된 앱(standalone) 안에서는 진입점이 무엇이든 원탭 마무리 화면으로 직행 —
            // 이미 피치를 보고 설치까지 완주한 유저에게 같은 피치를 반복하면 "왜 또 동의하지?"가 된다
            setStep(source === 'standalone' || isStandalone() ? 'finish' : 'pitch');
            setBusy(false);
            setPushModalVisible(true); // 다른 진입점(자동 게이트 등)이 겹쳐 열지 않게
            if (!shownLoggedRef.current) {
                shownLoggedRef.current = true;
                logPushFunnelEvent('shown', { source });
            }
            return () => setPushModalVisible(false);
        }
        shownLoggedRef.current = false;
        return undefined;
    }, [isOpen, source]);

    const handleAccept = useCallback(async () => {
        if (busy) return;
        setBusy(true);
        try {
            await logPushFunnelEvent('accepted', { source });
            const support = getPushSupport();

            if (support === 'ios-needs-install' || support === 'ios-needs-safari') {
                setNeedsSafari(support === 'ios-needs-safari');
                await logPushFunnelEvent('install_guide_shown', { source });
                setStep('ios-install');
                return;
            }
            if (support === 'android-needs-external') {
                await logPushFunnelEvent('install_guide_shown', { source });
                setStep('external');
                return;
            }
            if (support === 'unsupported') {
                setStep('unsupported');
                return;
            }

            // 여기서만 진짜 브라우저 권한 팝업이 뜬다
            const result = await window.Notification.requestPermission();
            if (result === 'granted') {
                await logPushFunnelEvent('granted', { source });
                // 구독 저장까지 성공해야 진짜 완료 — 실패를 삼키고 성공 화면을 띄우면
                // 유저는 켜졌다고 믿는데 실제 푸시는 오지 않는 최악의 상태가 된다
                const subscribed = await ensurePushSubscription({ source });
                setStep(subscribed.ok ? 'granted' : 'error');
            } else if (result === 'denied') {
                await logPushFunnelEvent('denied', { source });
                setStep('denied');
            } else {
                // 팝업을 닫기만 한 경우(default) — 브라우저 상태가 남지 않으므로 다음에 다시 물어볼 수 있다
                await logPushFunnelEvent('dismissed', { source });
                onClose();
            }
        } finally {
            setBusy(false);
        }
    }, [busy, onClose, source]);

    const handleClose = useCallback(() => {
        // 우리 화면 단계에서의 닫기/나중에 — 브라우저 권한은 소모되지 않았다
        if (step === 'pitch' || step === 'finish') {
            logPushFunnelEvent('later', { source });
        }
        onClose();
    }, [onClose, source, step]);

    const handleTestNotification = useCallback(() => {
        showTestNotification();
    }, []);

    // 모달이 떠 있는 동안 뒷페이지 스크롤 잠금
    useEffect(() => {
        if (!isOpen) return undefined;
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = previous; };
    }, [isOpen]);

    // 스레드 앱 안 — '홈 화면에 추가' 대신 스레드 메뉴로 사파리 여는 안내 (대표 2026-10-30)
    const threadsGuide = step === 'ios-install' && needsSafari && isThreadsInAppBrowser();
    const copy = threadsGuide
        ? { title: '사파리에서 알림을 켤 수 있어요', subtitle: '스레드 안에서는 알림을 켤 수 없어요' }
        : HEADER_COPY[step];

    if (!isOpen) return null;

    // body 포털 필수: 헤더(backdrop-filter)나 framer-motion(transform) 조상 안에서 렌더되면
    // position: fixed의 기준이 그 조상으로 바뀌어 모달이 엉뚱한 위치에 뜬다.
    // 오버레이는 overflow-y-auto + 패널 my-auto — 짧은 화면(가로모드 등)에서도 잘리지 않고 스크롤된다.
    // 닫힘은 즉시 언마운트: AnimatePresence 퇴장 방식은 내부 애니메이션과의 조합에서 제거가
    // 영구 블로킹되는 사례가 실측됐다 (오버레이가 화면을 덮은 채 남음 — 앱 전체 먹통).
    return createPortal(
        (
                <div
                    className="fixed inset-0 bg-black/90 sm:bg-black/80 sm:backdrop-blur-xl z-[9999] flex justify-center overflow-y-auto p-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))]"
                    role="dialog"
                    aria-modal="true"
                >
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0, y: 14 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        transition={{ type: 'spring', stiffness: 300, damping: 22 }}
                        className="my-auto h-fit bg-[#0B0B0B]/95 border border-[#262626] rounded-2xl w-full max-w-md overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
                    >
                        {/* 헤더 */}
                        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-[#262626]">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-xl bg-[#3182f6]/10 ring-1 ring-[#3182f6]/20">
                                    <Notification size={20} variant="Bold" className="text-[#3182f6]" style={{ color: '#3182f6' }} />
                                </div>
                                <div>
                                    <h2 className="text-base sm:text-lg font-semibold text-white">{copy.title}</h2>
                                    <p className="text-xs sm:text-sm text-[#A8A8A8] mt-0.5">{copy.subtitle}</p>
                                </div>
                            </div>
                            <motion.button
                                whileHover={{ scale: 1.05, backgroundColor: 'rgba(16, 16, 16, 0.8)' }}
                                whileTap={{ scale: 0.95 }}
                                onClick={handleClose}
                                aria-label="닫기"
                                className="p-2 hover:bg-[#101010] rounded-xl transition-all border border-[#262626]"
                            >
                                <CloseCircle size={20} variant="Linear" className="text-[#A8A8A8]" style={{ color: '#A8A8A8' }} />
                            </motion.button>
                        </div>

                        {/* 내용 */}
                        <div className="p-5 sm:p-6 space-y-4">
                            {step === 'pitch' && (
                                <>
                                    <LockScreenHyperframe />

                                    <motion.button
                                        whileHover={{ scale: 1.01 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={handleAccept}
                                        disabled={busy}
                                        className="w-full py-3.5 rounded-xl bg-[#3182f6] hover:bg-[#2b74dc] disabled:opacity-60 text-white text-sm font-semibold transition-colors"
                                    >
                                        {busy ? '잠시만요…' : '네, 알림 받을게요'}
                                    </motion.button>

                                    <p className="text-[11px] text-[#777777] text-center">
                                        무료 · 실시간 · 설정 &gt; 알림에서 언제든 끌 수 있어요
                                    </p>
                                </>
                            )}

                            {step === 'finish' && (
                                <>
                                    <div className="flex flex-col items-center gap-3 py-3">
                                        <motion.div
                                            initial={{ scale: 0 }}
                                            animate={{ scale: 1 }}
                                            transition={{ type: 'spring', stiffness: 260, damping: 16 }}
                                            className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#3182f6]/15 ring-1 ring-[#3182f6]/30"
                                        >
                                            <Notification size={28} variant="Bold" className="text-[#3182f6]" style={{ color: '#3182f6' }} />
                                        </motion.div>
                                        <p className="text-sm text-[#A8A8A8] text-center leading-relaxed">
                                            앱 설치까지 끝났어요. 아래 버튼을 누르면<br />
                                            마지막 허용 창이 딱 한 번 떠요 — <span className="text-white font-medium">허용</span>을 눌러주세요.
                                        </p>
                                    </div>
                                    <motion.button
                                        whileHover={{ scale: 1.01 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={handleAccept}
                                        disabled={busy}
                                        className="w-full py-3.5 rounded-xl bg-[#3182f6] hover:bg-[#2b74dc] disabled:opacity-60 text-white text-sm font-semibold transition-colors"
                                    >
                                        {busy ? '잠시만요…' : '알림 켜기'}
                                    </motion.button>
                                    <p className="text-[11px] text-[#777777] text-center">
                                        무료 · 실시간 · 설정 &gt; 알림에서 언제든 끌 수 있어요
                                    </p>
                                </>
                            )}

                            {step === 'ios-install' && (threadsGuide ? (
                                <>
                                    <ThreadsSafariGuide
                                        nextStep="사파리에서 같은 번호로 로그인하면 알림을 켤 수 있어요"
                                        copyHint="메뉴가 안 보이면 링크를 복사해 사파리 주소창에 붙여넣어 주세요"
                                    />
                                    <motion.button
                                        whileTap={{ scale: 0.98 }}
                                        onClick={onClose}
                                        className="w-full py-3 rounded-xl border border-[#262626] text-[#A8A8A8] hover:text-white text-sm font-medium transition-colors"
                                    >
                                        확인했어요
                                    </motion.button>
                                </>
                            ) : (
                                <IosInstallGuide needsSafari={needsSafari} onDone={onClose} />
                            ))}

                            {step === 'external' && (
                                <>
                                    <ExternalBrowserHandoff browserName="기본 브라우저" />
                                    <p className="text-[11px] text-[#777777] text-center leading-relaxed">
                                        기본 브라우저(Chrome 등)에서 열면 바로 알림을 켤 수 있어요
                                    </p>
                                    <motion.button
                                        whileTap={{ scale: 0.98 }}
                                        onClick={onClose}
                                        className="w-full py-3 rounded-xl border border-[#262626] text-[#A8A8A8] hover:text-white text-sm font-medium transition-colors"
                                    >
                                        확인했어요
                                    </motion.button>
                                </>
                            )}

                            {step === 'granted' && (
                                <>
                                    <div className="flex flex-col items-center gap-3 py-4">
                                        <motion.div
                                            initial={{ scale: 0 }}
                                            animate={{ scale: 1 }}
                                            transition={{ type: 'spring', stiffness: 260, damping: 16 }}
                                        >
                                            <TickCircle size={56} variant="Bold" className="text-[#00D87E]" style={{ color: '#00D87E' }} />
                                        </motion.div>
                                        <p className="text-sm text-[#A8A8A8] text-center">
                                            변화, 급증 알림을 실시간으로 받아보세요.<br />
                                            테스트 알림으로 바로 확인해볼 수 있어요.
                                        </p>
                                    </div>
                                    <div className="space-y-2">
                                        <motion.button
                                            whileHover={{ scale: 1.01 }}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={handleTestNotification}
                                            className="w-full py-3.5 rounded-xl bg-[#3182f6] hover:bg-[#2b74dc] text-white text-sm font-semibold transition-colors"
                                        >
                                            테스트 알림 보내기
                                        </motion.button>
                                        <motion.button
                                            whileTap={{ scale: 0.98 }}
                                            onClick={onClose}
                                            className="w-full py-2.5 rounded-xl text-[#A8A8A8] hover:text-white text-sm font-medium transition-colors"
                                        >
                                            닫기
                                        </motion.button>
                                    </div>
                                </>
                            )}

                            {step === 'denied' && (
                                <>
                                    <div className="bg-[#0F0F0F] rounded-2xl p-4 border border-[#262626] space-y-3">
                                        <div className="flex items-start gap-3">
                                            <Lock1 size={20} variant="Bold" className="text-[#FFB800] mt-0.5 shrink-0" style={{ color: '#FFB800' }} />
                                            <div className="text-xs text-[#A8A8A8] leading-relaxed">
                                                <p className="text-sm font-medium text-white mb-1">브라우저에서 직접 허용이 필요해요</p>
                                                <p>1. 주소창 왼쪽의 <span className="text-white">자물쇠(🔒) 아이콘</span>을 눌러주세요</p>
                                                <p>2. <span className="text-white">알림</span> 항목을 <span className="text-white">허용</span>으로 바꿔주세요</p>
                                                <p className="mt-1 text-[#777777]">iPhone은 홈 화면에 추가한 뒤 설정 &gt; 알림에서 켤 수 있어요</p>
                                            </div>
                                        </div>
                                    </div>
                                    <motion.button
                                        whileTap={{ scale: 0.98 }}
                                        onClick={onClose}
                                        className="w-full py-3 rounded-xl border border-[#262626] text-[#A8A8A8] hover:text-white text-sm font-medium transition-colors"
                                    >
                                        확인했어요
                                    </motion.button>
                                </>
                            )}

                            {step === 'error' && (
                                <>
                                    <p className="text-sm text-[#A8A8A8] text-center py-4">
                                        알림 허용은 됐는데 구독 저장이 잠시 실패했어요.<br />
                                        다시 시도하거나, 나중에 설정 &gt; 알림에서 켤 수 있어요.
                                    </p>
                                    <div className="space-y-2">
                                        <motion.button
                                            whileHover={{ scale: 1.01 }}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={handleAccept}
                                            disabled={busy}
                                            className="w-full py-3.5 rounded-xl bg-[#3182f6] hover:bg-[#2b74dc] disabled:opacity-60 text-white text-sm font-semibold transition-colors"
                                        >
                                            {busy ? '잠시만요…' : '다시 시도'}
                                        </motion.button>
                                        <motion.button
                                            whileTap={{ scale: 0.98 }}
                                            onClick={onClose}
                                            className="w-full py-2.5 rounded-xl text-[#A8A8A8] hover:text-white text-sm font-medium transition-colors"
                                        >
                                            닫기
                                        </motion.button>
                                    </div>
                                </>
                            )}

                            {step === 'unsupported' && (
                                <>
                                    <p className="text-sm text-[#A8A8A8] text-center py-4">
                                        현재 브라우저는 웹 알림을 지원하지 않아요.<br />
                                        Chrome, Safari 최신 버전에서 이용할 수 있어요.
                                    </p>
                                    <motion.button
                                        whileTap={{ scale: 0.98 }}
                                        onClick={onClose}
                                        className="w-full py-3 rounded-xl border border-[#262626] text-[#A8A8A8] hover:text-white text-sm font-medium transition-colors"
                                    >
                                        닫기
                                    </motion.button>
                                </>
                            )}
                        </div>
                    </motion.div>
                </div>
        ),
        document.body,
    );
};

export default PushPrePermissionModal;
